import TimePicker from 'react-time-picker';
import Split from '../Shared/Split';
import { Alert, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { actions } from '../../context';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import {
    utcToPracticeTime,
    practiceTimeToUtc,
    timeZoneAbbr,
} from '../Shared/PracticeTimeZone';

// The parent modal only mounts this component once its window data has loaded, and fully
// unmounts it between opens - so plain useState-from-props is safe here (no effect syncing
// needed, and no risk of a re-render clobbering in-progress edits).
const AttachmentScheduleForm = (props) => {
    const dispatch = useDispatch();
    const { practiceId, practiceTimeZone, window: windowData, cancel } = props;
    const tzLabel = timeZoneAbbr(practiceTimeZone);

    const [windowStart, setWindowStart] = useState(
        utcToPracticeTime(windowData.attachmentSyncWindowStart, practiceTimeZone)
    );
    const [windowEnd, setWindowEnd] = useState(
        utcToPracticeTime(windowData.attachmentSyncWindowEnd, practiceTimeZone)
    );
    const [pristine, setPristine] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const updateStart = (value) => {
        setPristine(false);
        setWindowStart(value);
    };
    const updateEnd = (value) => {
        setPristine(false);
        setWindowEnd(value);
    };
    const clear = () => {
        setPristine(false);
        setWindowStart(null);
        setWindowEnd(null);
    };

    const validationError =
        !!windowStart !== !!windowEnd
            ? 'Set both a start and an end time, or clear both.'
            : windowStart && windowStart === windowEnd
            ? 'Start and end times cannot be equal.'
            : null;
    const wrapsMidnight =
        windowStart && windowEnd && windowStart > windowEnd;

    const submit = (e) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitting(true);
        const payload = {
            attachmentSyncWindowStart: practiceTimeToUtc(windowStart, practiceTimeZone),
            attachmentSyncWindowEnd: practiceTimeToUtc(windowEnd, practiceTimeZone),
        };
        dispatch(actions.practice.saveAttachmentSyncWindow(practiceId, payload))
            .then(() => {
                cancel();
                dispatch(actions.toggle.triggerPracticeRefresh());
            })
            .catch((err) => {
                setSubmitting(false);
                setSubmitError(err?.message || 'Save failed. Please try again.');
            });
    };

    return (
        <form onSubmit={submit}>
            <Alert color="warning" className="mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                Please consider the practice's business hours before scheduling.
            </Alert>
            <div className="w-100 mb-2 d-flex">
                <div className="w-50">
                    <h5>Start Time ({tzLabel})</h5>
                    <TimePicker
                        value={windowStart}
                        onChange={updateStart}
                        disableClock={true}
                        format="h:mm a"
                        className="text-dark bg-light p-2 rounded"
                    />
                </div>
                <div className="w-50">
                    <h5>End Time ({tzLabel})</h5>
                    <TimePicker
                        value={windowEnd}
                        onChange={updateEnd}
                        disableClock={true}
                        format="h:mm a"
                        className="text-dark bg-light p-2 rounded"
                    />
                </div>
            </div>
            <div className="mb-3">
                {submitError ? (
                    <span className="text-danger">{submitError}</span>
                ) : validationError ? (
                    <span className="text-danger">{validationError}</span>
                ) : wrapsMidnight ? (
                    <span className="text-warning">
                        This window crosses midnight ({tzLabel}).
                    </span>
                ) : !windowStart && !windowEnd ? (
                    <span className="text-light op-6">
                        No window set; automatic extraction is not restricted
                        to a time window.
                    </span>
                ) : null}
            </div>
            <Split>
                <Button
                    color="danger"
                    className="mx-1"
                    disabled={!windowStart && !windowEnd}
                    onClick={clear}>
                    Clear
                </Button>
                <div>
                    <Button color="danger" className="mx-1" onClick={cancel}>
                        Cancel
                    </Button>
                    <Button
                        color="success"
                        disabled={pristine || submitting || !!validationError}
                        type="submit">
                        Submit
                    </Button>
                </div>
            </Split>
        </form>
    );
};

export default AttachmentScheduleForm;
